import { useState } from 'react';
import { viewScan, type LedgerEntry, type OpsSession, type ScanTone } from '@ecobox/firebase';
import { Badge, Button, type BadgeTone } from '@ecobox/ui';

const TONE_BADGE: Record<ScanTone, BadgeTone> = {
  ok: 'success',
  wait: 'warning',
  warn: 'warning',
  bad: 'danger',
};
const PAGE = 100;

/** The scans of a session, newest first, each with its own state (the same words as Load and Receive). */
export function ScanRows({
  session,
  ledger,
  onRemove,
  onRelease,
  label = 'Scans',
}: {
  session: OpsSession;
  ledger: ReadonlyMap<string, LedgerEntry>;
  /** Offered for scans that have not gone yet. */
  onRemove?: (barcode: string) => void;
  /** Sends a held box after all. */
  onRelease?: (barcode: string) => void;
  label?: string;
}) {
  const [shown, setShown] = useState(PAGE);
  const rows = [...session.scans].reverse();
  if (rows.length === 0) {
    return (
      <p className="ops-muted" data-tour="scan-list">
        Nothing scanned yet. Scan a box and it shows here at once.
      </p>
    );
  }
  return (
    <>
      <ul className="ops-list" aria-label={label} data-tour="scan-list">
        {rows.slice(0, shown).map((scan) => {
          const view = viewScan(scan, scan.commandId ? ledger.get(scan.commandId) : undefined);
          const canRemove =
            scan.status === 'buffered' || scan.status === 'held' || scan.status === 'invalid';
          return (
            <li
              key={scan.barcode}
              className="ops-row"
              data-tone={view.tone === 'wait' ? 'waiting' : view.tone}
            >
              <div className="ops-row__top">
                <span className="ops-code">{scan.barcode}</span>
                <Badge tone={TONE_BADGE[view.tone]}>{view.label}</Badge>
              </div>
              {view.detail ? <div className="ops-detail">{view.detail}</div> : null}
              <div className="ops-row__actions">
                {scan.status === 'held' && onRelease ? (
                  <Button size="lg" variant="secondary" onClick={() => onRelease(scan.barcode)}>
                    Send anyway
                  </Button>
                ) : null}
                {canRemove && onRemove ? (
                  <Button size="lg" variant="ghost" onClick={() => onRemove(scan.barcode)}>
                    Remove
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {rows.length > shown ? (
        <Button size="lg" variant="secondary" onClick={() => setShown(shown + PAGE)}>
          Show more ({rows.length - shown} older)
        </Button>
      ) : null}
    </>
  );
}
